/**
 * Suites — calculs purs.
 *
 * Trois familles « exactes » réglées au curseur (pas décimaux, donc
 * fractions exactes) : arithmétique uₙ₊₁ = uₙ + r, géométrique uₙ₊₁ = q·uₙ,
 * arithmético-géométrique uₙ₊₁ = a·uₙ + b. Pour elles, formule explicite,
 * sens de variation, limite et sommes sont DÉMONTRÉS (formules du cours).
 * La quatrième, uₙ₊₁ = f(uₙ) avec f tapée par l'élève, n'est étudiée que
 * numériquement : on dit « semble converger », jamais « converge ».
 */

import {
  add,
  div,
  exact,
  frac,
  fromDecimal,
  isZero,
  mul,
  sub,
  value,
  type ExactNumber,
  type Frac,
} from '../../ui/exact';
import { evalExpr } from '@jeraisonne/db/shared/expr';
import { formatNumber } from '../../ui/format';

export type SeqKind = 'arith' | 'geom' | 'arigeo' | 'recur';
export type ExactKind = Exclude<SeqKind, 'recur'>;

export interface SeqParams {
  u0: number;
  r: number;
  q: number;
  a: number;
  b: number;
}

export const RANGES = {
  u0: { min: -10, max: 10, step: 0.5 },
  r: { min: -5, max: 5, step: 0.5 },
  q: { min: -3, max: 3, step: 0.1 },
  a: { min: -2, max: 2, step: 0.1 },
  b: { min: -10, max: 10, step: 0.5 },
} as const;

export type StepFn = (x: number) => number;

/** uₙ₊₁ = step(uₙ) */
export function stepOf(kind: ExactKind, p: SeqParams): StepFn {
  if (kind === 'arith') return (x) => x + p.r;
  if (kind === 'geom') return (x) => p.q * x;
  return (x) => p.a * x + p.b;
}

/** u₀ … uₙ ; après un terme non défini, les suivants le sont aussi (NaN). */
export function computeTerms(step: StepFn, u0: number, n: number): number[] {
  const out = [u0];
  let x = u0;
  for (let i = 0; i < n; i++) {
    x = Number.isFinite(x) ? step(x) : NaN;
    out.push(x);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Écriture exacte

const F = (v: number) => fromDecimal(v);
const tex = (f: Frac) => exact(f).tex;

/** puissance écrite proprement : 2^n, (−0,5)^n, 0{,}5^n */
function powTex(base: Frac, exp: string): string {
  const b = tex(base);
  return base.p < 0 || b.includes('frac') ? `\\left(${b}\\right)^{${exp}}` : `${b}^{${exp}}`;
}

/** « 3 », « 3 − 2n », « −n » : constante + coef × corps, signes propres */
function linearTex(constant: Frac, coef: Frac, body: string): string {
  const parts: string[] = [];
  if (!isZero(constant)) parts.push(tex(constant));
  if (!isZero(coef)) {
    const neg = coef.p < 0;
    const abs = frac(Math.abs(coef.p), coef.q);
    const c =
      abs.p === 1 && abs.q === 1
        ? body
        : `${tex(abs)}${body.startsWith('\\left') || /^\d/.test(body) ? ' \\times ' : ''}${body}`;
    if (parts.length === 0) parts.push(neg ? `-${c}` : c);
    else parts.push(`${neg ? '-' : '+'} ${c}`);
  }
  return parts.length ? parts.join(' ') : '0';
}

/** limite ℓ = b / (1 − a) de la suite arithmético-géométrique (a ≠ 1) */
export function fixedPointAG(p: SeqParams): Frac | null {
  const a = F(p.a);
  if (a.p === a.q) return null;
  return div(F(p.b), sub(frac(1), a));
}

export interface Explicit {
  /** « u_n = … » en KaTeX */
  tex: string;
  /** à partir de quel rang la formule vaut (1 pour q = 0 ou a = 0) */
  from: number;
}

export function explicitFormula(kind: ExactKind, p: SeqParams): Explicit {
  const u0 = F(p.u0);
  if (kind === 'arith') return { tex: `u_n = ${linearTex(u0, F(p.r), 'n')}`, from: 0 };
  if (kind === 'geom') {
    const q = F(p.q);
    if (isZero(u0)) return { tex: 'u_n = 0', from: 0 };
    if (isZero(q)) return { tex: 'u_n = 0', from: 1 };
    if (q.p === q.q) return { tex: `u_n = ${tex(u0)}`, from: 0 };
    return { tex: `u_n = ${linearTex(frac(0), u0, powTex(q, 'n'))}`, from: 0 };
  }
  const a = F(p.a);
  const b = F(p.b);
  if (a.p === a.q) return { tex: `u_n = ${linearTex(u0, b, 'n')}`, from: 0 };
  if (isZero(a)) return { tex: `u_n = ${tex(b)}`, from: 1 };
  const l = fixedPointAG(p)!;
  const c = sub(u0, l);
  if (isZero(c)) return { tex: `u_n = ${tex(l)}`, from: 0 };
  return { tex: `u_n = ${linearTex(l, c, powTex(a, 'n'))}`, from: 0 };
}

// ---------------------------------------------------------------------------
// Sens de variation et limite (familles exactes)

export type Monotony = 'increasing' | 'decreasing' | 'constant' | 'constantFrom1' | 'none';

const sign = (f: Frac) => Math.sign(f.p);

export function monotony(kind: ExactKind, p: SeqParams): Monotony {
  const u0 = F(p.u0);
  if (kind === 'arith') return p.r > 0 ? 'increasing' : p.r < 0 ? 'decreasing' : 'constant';
  if (kind === 'geom') {
    const q = F(p.q);
    if (isZero(u0) || q.p === q.q) return 'constant';
    if (isZero(q)) return 'constantFrom1';
    if (q.p < 0) return 'none';
    // uₙ₊₁ − uₙ = u₀ qⁿ (q − 1)
    const s = sign(u0) * sign(sub(q, frac(1)));
    return s > 0 ? 'increasing' : 'decreasing';
  }
  const a = F(p.a);
  if (a.p === a.q) return p.b > 0 ? 'increasing' : p.b < 0 ? 'decreasing' : 'constant';
  if (isZero(a)) return p.u0 === p.b ? 'constant' : 'constantFrom1';
  const c = sub(u0, fixedPointAG(p)!);
  if (isZero(c)) return 'constant';
  if (a.p < 0) return 'none';
  // uₙ₊₁ − uₙ = c aⁿ (a − 1)
  return sign(c) * sign(sub(a, frac(1))) > 0 ? 'increasing' : 'decreasing';
}

export type Limit =
  | { type: 'finite'; value: ExactNumber; constant: boolean }
  | { type: 'plusInf' }
  | { type: 'minusInf' }
  | { type: 'none'; why: 'alternate' | 'alternateGrow' };

export function limitOf(kind: ExactKind, p: SeqParams): Limit {
  const u0 = F(p.u0);
  const arithLike = (r: number): Limit =>
    r > 0
      ? { type: 'plusInf' }
      : r < 0
        ? { type: 'minusInf' }
        : { type: 'finite', value: exact(u0), constant: true };
  if (kind === 'arith') return arithLike(p.r);
  if (kind === 'geom') {
    const q = F(p.q);
    if (isZero(u0)) return { type: 'finite', value: exact(frac(0)), constant: true };
    if (isZero(q)) return { type: 'finite', value: exact(frac(0)), constant: true };
    if (q.p === q.q) return { type: 'finite', value: exact(u0), constant: true };
    if (Math.abs(q.p) < q.q) return { type: 'finite', value: exact(frac(0)), constant: false };
    if (q.p > 0) return u0.p > 0 ? { type: 'plusInf' } : { type: 'minusInf' };
    return { type: 'none', why: q.p === -q.q ? 'alternate' : 'alternateGrow' };
  }
  const a = F(p.a);
  if (a.p === a.q) return arithLike(p.b);
  if (isZero(a)) return { type: 'finite', value: exact(F(p.b)), constant: true };
  const l = fixedPointAG(p)!;
  const c = sub(u0, l);
  if (isZero(c)) return { type: 'finite', value: exact(l), constant: true };
  if (Math.abs(a.p) < a.q) return { type: 'finite', value: exact(l), constant: false };
  if (a.p > 0) return c.p > 0 ? { type: 'plusInf' } : { type: 'minusInf' };
  return { type: 'none', why: a.p === -a.q ? 'alternate' : 'alternateGrow' };
}

/** Phrase qui justifie la limite, avec le théorème du cours. */
export function limitReason(kind: ExactKind, p: SeqParams): string {
  const L = limitOf(kind, p);
  const n = (v: number) => formatNumber(v, 3);
  if (kind === 'arith') {
    if (p.r === 0) return 'r = 0 : la suite est constante.';
    return `Suite arithmétique de raison r = ${n(p.r)} ${p.r > 0 ? '> 0' : '< 0'} : elle tend vers ${
      p.r > 0 ? '+∞' : '−∞'
    }.`;
  }
  if (kind === 'geom') {
    if (p.u0 === 0) return 'u₀ = 0 : tous les termes sont nuls.';
    if (p.q === 0) return 'q = 0 : tous les termes sont nuls à partir du rang 1.';
    if (p.q === 1) return 'q = 1 : la suite est constante.';
    if (L.type === 'finite')
      return `−1 < q = ${n(p.q)} < 1 : qⁿ tend vers 0, donc uₙ = u₀qⁿ aussi.`;
    if (L.type === 'plusInf' || L.type === 'minusInf')
      return `q = ${n(p.q)} > 1 : qⁿ tend vers +∞, et u₀ ${p.u0 > 0 ? '> 0' : '< 0'} donne ${
        L.type === 'plusInf' ? '+∞' : '−∞'
      }.`;
    return p.q === -1
      ? 'q = −1 : les termes valent u₀ et −u₀ tour à tour, pas de limite.'
      : `q = ${n(p.q)} < −1 : les termes changent de signe et grandissent, pas de limite.`;
  }
  if (p.a === 1)
    return p.b === 0
      ? 'a = 1 et b = 0 : la suite est constante.'
      : `a = 1 : c’est une suite arithmétique de raison b = ${n(p.b)} ${p.b > 0 ? '> 0' : '< 0'}, elle tend vers ${
          p.b > 0 ? '+∞' : '−∞'
        }.`;
  if (p.a === 0) return `a = 0 : tous les termes valent b = ${n(p.b)} à partir du rang 1.`;
  const lf = fixedPointAG(p)!;
  const l = exact(lf).plain;
  // vₙ = uₙ − ℓ écrit sans « − − 6 » ni « − 0 »
  const v = isZero(lf)
    ? 'vₙ = uₙ'
    : `vₙ = uₙ ${lf.p < 0 ? '+' : '−'} ${exact(frac(Math.abs(lf.p), lf.q)).plain}`;
  if (L.type === 'finite' && L.constant) return `u₀ = ℓ = ${l} : la suite est constante.`;
  if (L.type === 'finite')
    return `${v} est géométrique de raison a = ${n(p.a)}, avec −1 < a < 1 : vₙ tend vers 0, donc uₙ tend vers ℓ = ${l}.`;
  if (L.type === 'plusInf' || L.type === 'minusInf')
    return `${v} est géométrique de raison a = ${n(p.a)} > 1, avec v₀ ${
      L.type === 'plusInf' ? '> 0' : '< 0'
    } : uₙ tend vers ${L.type === 'plusInf' ? '+∞' : '−∞'}.`;
  return p.a === -1
    ? `a = −1 : les termes valent u₀ et ${formatNumber(p.b - p.u0, 3)} tour à tour, pas de limite.`
    : `${v} est géométrique de raison a = ${n(p.a)} < −1 : pas de limite.`;
}

/** Suite auxiliaire vₙ = uₙ − ℓ (arithmético-géométrique, a ∉ {0, 1}). */
export function auxiliary(p: SeqParams): { l: ExactNumber; v0: ExactNumber; tex: string } | null {
  const a = F(p.a);
  if (isZero(a) || a.p === a.q) return null;
  const l = fixedPointAG(p)!;
  const v0 = sub(F(p.u0), l);
  const shift = isZero(l) ? 'u_n' : `u_n ${l.p < 0 ? '+' : '-'} ${tex(frac(Math.abs(l.p), l.q))}`;
  return {
    l: exact(l),
    v0: exact(v0),
    tex: `v_n = ${shift} = ${linearTex(frac(0), v0, powTex(a, 'n'))}`,
  };
}

/** relation de récurrence en KaTeX : u_{n+1} = 0{,}5u_n + 3 */
export function recurrenceTex(kind: ExactKind, p: SeqParams): string {
  const coefU = (c: Frac) =>
    isZero(c) ? '' : c.p === c.q ? 'u_n' : c.p === -c.q ? '-u_n' : `${tex(c)}\\,u_n`;
  const plus = (head: string, c: Frac) =>
    isZero(c)
      ? head || '0'
      : head
        ? `${head} ${c.p < 0 ? '-' : '+'} ${tex(frac(Math.abs(c.p), c.q))}`
        : tex(c);
  if (kind === 'arith') return `u_{n+1} = ${plus('u_n', F(p.r))}`;
  if (kind === 'geom') return `u_{n+1} = ${coefU(F(p.q)) || '0'}`;
  return `u_{n+1} = ${plus(coefU(F(p.a)), F(p.b))}`;
}

/**
 * uₙ exact (fraction) tant que les entiers restent sûrs, sinon null :
 * avec des raisons décimales, les dénominateurs grossissent vite.
 */
export function exactTerm(kind: ExactKind, p: SeqParams, n: number): ExactNumber | null {
  // suite arithmétique : formule directe u₀ + n·r (n peut valoir des millions)
  const r = kind === 'arith' ? p.r : kind === 'arigeo' && p.a === 1 ? p.b : null;
  if (r !== null) return exact(add(F(p.u0), mul(frac(n), F(r))));
  let u = F(p.u0);
  const q = F(p.q);
  const a = F(p.a);
  const b = F(p.b);
  for (let i = 0; i < n; i++) {
    u = kind === 'geom' ? mul(q, u) : add(mul(a, u), b);
    if (Math.abs(u.p) > 2 ** 40 || u.q > 2 ** 40) return null;
  }
  return exact(u);
}

// ---------------------------------------------------------------------------
// Sommes, seuil, bande ε

/** u₀ + … + uₙ : formule KaTeX du cours et valeur (exacte si arithmétique). */
export function sumOf(
  kind: 'arith' | 'geom',
  p: SeqParams,
  n: number,
): { formula: string; value: number; exact: ExactNumber | null } {
  if (kind === 'arith') {
    // (n + 1)(u₀ + uₙ) / 2
    const un = add(F(p.u0), mul(frac(n), F(p.r)));
    const s = div(mul(frac(n + 1), add(F(p.u0), un)), frac(2));
    return {
      formula: `u_0 + \\dots + u_{${n}} = \\frac{(${n} + 1)(u_0 + u_{${n}})}{2}`,
      value: value(s),
      exact: exact(s),
    };
  }
  if (p.q === 1)
    return {
      formula: `u_0 + \\dots + u_{${n}} = (${n} + 1)\\,u_0`,
      value: (n + 1) * p.u0,
      exact: exact(mul(frac(n + 1), F(p.u0))),
    };
  const v = (p.u0 * (1 - p.q ** (n + 1))) / (1 - p.q);
  return {
    formula: `u_0 + \\dots + u_{${n}} = u_0 \\times \\frac{1 - q^{${n + 1}}}{1 - q}`,
    value: v,
    exact: null,
  };
}

/**
 * Seuil exact d'une suite arithmétique (ou arithmético-géométrique avec a = 1) :
 * uₙ = u₀ + n·r dépasse T dès n > (T − u₀)/r. null pour les autres familles.
 */
export function arithmeticThreshold(
  kind: ExactKind,
  p: SeqParams,
  threshold: number,
  dir: 1 | -1,
): number | null {
  const r = kind === 'arith' ? p.r : kind === 'arigeo' && p.a === 1 ? p.b : null;
  if (r === null || r === 0 || Math.sign(r) !== dir) return null;
  const k = div(sub(F(threshold), F(p.u0)), F(r));
  if (k.p < 0) return 0;
  return Math.floor(k.p / k.q) + 1;
}

/** Premier rang n tel que uₙ > A (dir 1) ou uₙ < A (dir −1), au plus `max`. */
export function firstRank(
  step: StepFn,
  u0: number,
  threshold: number,
  dir: 1 | -1,
  max = 100000,
): number | null {
  let x = u0;
  for (let n = 0; n <= max; n++) {
    if (!Number.isFinite(x)) return null;
    if (dir === 1 ? x > threshold : x < threshold) return n;
    x = step(x);
  }
  return null;
}

/**
 * Familles exactes, suite convergente non constante : |uₙ − ℓ| = |v₀|·|k|ⁿ
 * (k = q ou a), qui décroît. Premier rang où c'est < ε, calculé en entiers
 * exacts (BigInt) : pas d'erreur d'arrondi pile sur la frontière.
 */
export function exactBandRank(kind: ExactKind, p: SeqParams, eps: number): number | null {
  const L = limitOf(kind, p);
  if (L.type !== 'finite' || L.constant) return null;
  let c: Frac;
  let k: Frac;
  if (kind === 'geom') {
    c = F(p.u0);
    k = F(p.q);
  } else if (kind === 'arigeo') {
    c = sub(F(p.u0), fixedPointAG(p)!);
    k = F(p.a);
  } else return null;
  const e = F(eps);
  const cp = BigInt(Math.abs(c.p));
  const cq = BigInt(c.q);
  const kp = BigInt(Math.abs(k.p));
  const kq = BigInt(k.q);
  const ep = BigInt(e.p);
  const eq = BigInt(e.q);
  // |c|·|k|ⁿ < ε  ⇔  cp·kpⁿ·eq < ep·cq·kqⁿ
  let num = cp * eq;
  let den = ep * cq;
  for (let n = 0; n <= 100000; n++) {
    if (num < den) return n;
    num *= kp;
    den *= kq;
  }
  return null;
}

/** Rang à partir duquel TOUS les termes calculés sont dans ]ℓ − ε ; ℓ + ε[. */
export function rankInBand(terms: readonly number[], l: number, eps: number): number | null {
  let lastOut = -1;
  for (let i = 0; i < terms.length; i++) {
    const t = terms[i]!;
    if (!Number.isFinite(t) || Math.abs(t - l) >= eps) lastOut = i;
  }
  return lastOut + 1 < terms.length ? lastOut + 1 : null;
}

/** Algorithme de seuil, en Python comme au lycée. */
export function thresholdProgram(
  kind: SeqKind,
  p: SeqParams,
  threshold: number,
  dir: 1 | -1,
  /** expression compilée de f (syntaxe de l'évaluateur), pour uₙ₊₁ = f(uₙ) */
  recurExpr?: string,
): string {
  const py = (v: number) => String(Number(v.toFixed(3)));
  const update =
    kind === 'arith'
      ? `u = u ${p.r < 0 ? '-' : '+'} ${py(Math.abs(p.r))}`
      : kind === 'geom'
        ? `u = ${py(p.q)} * u`
        : kind === 'arigeo'
          ? `u = ${py(p.a)} * u ${p.b < 0 ? '-' : '+'} ${py(Math.abs(p.b))}`
          : 'u = f(u)';
  const lines = [
    ...(kind === 'recur' && recurExpr
      ? ['from math import *', '', 'def f(x):', `    return ${pythonExpr(recurExpr)}`, '']
      : []),
    `u = ${py(p.u0)}`,
    'n = 0',
    `while u ${dir === 1 ? '<=' : '>='} ${py(threshold)}:`,
    `    ${update}`,
    '    n = n + 1',
    'print(n)',
  ];
  return lines.join('\n');
}

/** expression de l'évaluateur → Python (math) : ^ → **, ln → log */
export function pythonExpr(expr: string): string {
  return expr
    .replace(/\^/g, '**')
    .replace(/\bln\(/g, 'log(')
    .replace(/([+\-*/])/g, ' $1 ')
    .replace(/ \* {2}\* /g, ' ** ')
    .replace(/\s+/g, ' ')
    .replace(/\( /g, '(')
    .replace(/ \)/g, ')')
    .trim();
}

// ---------------------------------------------------------------------------
// uₙ₊₁ = f(uₙ) : étude numérique

/**
 * uₙ₊₁ = f(uₙ) pour une expression compilée : garde ±∞ (un dépassement n'est
 * pas une sortie du domaine), NaN seulement hors du domaine.
 */
export function recurStep(expr: string): StepFn {
  return (x) => {
    try {
      return evalExpr(expr, { x });
    } catch {
      return NaN;
    }
  };
}

export type RecurVerdict =
  | {
      type: 'converges';
      value: number;
      derivative: number | null;
      /** f(u₀) = u₀ : suite constante dès le départ */
      constant: boolean;
      /** premier rang où la suite vaut exactement sa limite (en flottants) */
      reachedAt: number | null;
    }
  | { type: 'plusInf' }
  | { type: 'minusInf' }
  /** |uₙ| grandit en changeant de signe */
  | { type: 'alternateGrow' }
  | { type: 'cycle2'; values: [number, number] }
  | { type: 'undefined'; at: number }
  | { type: 'unclear'; lastStep: number; trend: 'up' | 'down' | 'mixed' };

export function derivativeAt(f: StepFn, x: number): number | null {
  const h = 1e-5;
  const d = (f(x + h) - f(x - h)) / (2 * h);
  return Number.isFinite(d) ? d : null;
}

export function analyseRecur(f: StepFn, u0: number, horizon = 3000): RecurVerdict {
  const t = [u0];
  let x = u0;
  const huge = (v: number) => !Number.isNaN(v) && Math.abs(v) > 1e12;
  for (let i = 0; i < horizon; i++) {
    if (!Number.isFinite(x) || huge(x)) break;
    x = f(x);
    t.push(x);
  }
  const lastIndex = t.length - 1;
  const last = t[lastIndex]!;
  if (Number.isNaN(last)) return { type: 'undefined', at: lastIndex };
  if (huge(last)) {
    const prev = t[lastIndex - 1] ?? 0;
    if (Math.sign(prev) === -Math.sign(last) && Math.abs(prev) > 1)
      return { type: 'alternateGrow' };
    return last > 0 ? { type: 'plusInf' } : { type: 'minusInf' };
  }
  const prev = t[lastIndex - 1] ?? last;
  const prev2 = t[lastIndex - 2] ?? prev;
  const close = (a: number, b: number) => Math.abs(a - b) < 1e-9 * Math.max(1, Math.abs(a));
  if (close(last, prev)) {
    const reached = t.findIndex((v) => v === last);
    return {
      type: 'converges',
      value: last,
      derivative: derivativeAt(f, last),
      constant: t.length > 1 && close(t[1]!, t[0]!),
      reachedAt: reached >= 0 ? reached : null,
    };
  }
  if (close(last, prev2))
    return { type: 'cycle2', values: [Math.min(prev, last), Math.max(prev, last)] };
  // tendance sur les 50 derniers termes
  const tail = t.slice(-50);
  let up = true;
  let down = true;
  for (let i = 1; i < tail.length; i++) {
    if (tail[i]! <= tail[i - 1]!) up = false;
    if (tail[i]! >= tail[i - 1]!) down = false;
  }
  return {
    type: 'unclear',
    lastStep: Math.abs(last - prev),
    trend: up ? 'up' : down ? 'down' : 'mixed',
  };
}

/**
 * Solutions de f(x) = x dans [lo ; hi] (changements de signe, puis
 * dichotomie). 'all' si f(x) = x presque partout (f = identité).
 */
export function fixedPoints(f: StepFn, lo = -10, hi = 10, samples = 4000): number[] | 'all' {
  const g = (x: number) => f(x) - x;
  const out: number[] = [];
  const h = (hi - lo) / samples;
  let zeros = 0;
  for (let i = 0; i <= samples; i += 40) if (Math.abs(g(lo + i * h)) < 1e-12) zeros++;
  if (zeros > (samples / 40) * 0.9) return 'all';
  let x0 = lo;
  let g0 = g(x0);
  for (let i = 1; i <= samples; i++) {
    const x1 = lo + i * h;
    const g1 = g(x1);
    if (Number.isFinite(g0) && Number.isFinite(g1)) {
      if (g0 === 0) out.push(x0);
      else if (g0 * g1 < 0) {
        let a = x0;
        let b = x1;
        for (let k = 0; k < 60; k++) {
          const m = (a + b) / 2;
          const gm = g(m);
          if (!Number.isFinite(gm)) break;
          if (g(a) * gm <= 0) b = m;
          else a = m;
        }
        out.push((a + b) / 2);
      }
    }
    x0 = x1;
    g0 = g1;
  }
  // doublons (racine posée sur un point de l'échantillon)
  return out.filter((v, i) => i === 0 || Math.abs(v - out[i - 1]!) > 1e-6);
}

export const RECUR_PRESETS: ReadonlyArray<{ label: string; formula: string; u0: number }> = [
  { label: '√(x + 2)', formula: '√(x + 2)', u0: 0 },
  { label: 'Héron (√2)', formula: '(x + 2/x)/2', u0: 1 },
  { label: 'cos(x)', formula: 'cos(x)', u0: 1 },
  { label: 'x² + 0,1', formula: 'x² + 0,1', u0: 0 },
  { label: 'Cycle', formula: '3,2x(1 − x)', u0: 0.2 },
];

// ---------------------------------------------------------------------------
// Toile d'araignée

/** (u₀ ; 0) → (u₀ ; u₁) → (u₁ ; u₁) → (u₁ ; u₂) → … */
export function cobweb(terms: readonly number[]): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  const first = terms[0];
  if (first === undefined || !Number.isFinite(first)) return pts;
  pts.push([first, 0]);
  for (let i = 0; i + 1 < terms.length; i++) {
    const x = terms[i]!;
    const y = terms[i + 1]!;
    if (!Number.isFinite(y)) break;
    pts.push([x, y], [y, y]);
  }
  return pts;
}

/** Fenêtre [lo ; hi] qui contient les valeurs (finies), marge de 8 %, largeur ≥ 1. */
export function fitRange(values: readonly number[]): [number, number] {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return [-1, 1];
  let lo = Math.min(...finite);
  let hi = Math.max(...finite);
  if (hi - lo < 1) {
    const c = (lo + hi) / 2;
    lo = c - 0.5;
    hi = c + 0.5;
  }
  const pad = (hi - lo) * 0.08;
  return [lo - pad, hi + pad];
}

// ---------------------------------------------------------------------------
// Défi : retrouver la suite d'après son nuage

export interface SeqTarget {
  kind: 'arith' | 'geom';
  u0: number;
  /** raison r ou q */
  ratio: number;
}

export function pickTarget(rng: () => number, previous?: SeqTarget): SeqTarget {
  const pick = <T>(xs: readonly T[]) => xs[Math.min(xs.length - 1, Math.floor(rng() * xs.length))]!;
  for (let i = 0; i < 20; i++) {
    const kind = rng() < 0.5 ? 'arith' : 'geom';
    const t: SeqTarget =
      kind === 'arith'
        ? {
            kind,
            u0: pick([-4, -2, -1, 0, 1, 2, 3, 5]),
            ratio: pick([-3, -2, -1.5, -1, -0.5, 0.5, 1, 1.5, 2, 3]),
          }
        : { kind, u0: pick([-2, -1, 0.5, 1, 2, 3]), ratio: pick([-2, -0.5, 0.5, 1.5, 2, 3]) };
    if (!previous || t.kind !== previous.kind || t.u0 !== previous.u0 || t.ratio !== previous.ratio)
      return t;
  }
  return { kind: 'arith', u0: 1, ratio: 2 };
}

export function matchesTarget(kind: SeqKind, p: SeqParams, t: SeqTarget): boolean {
  if (kind !== t.kind || p.u0 !== t.u0) return false;
  return kind === 'arith' ? p.r === t.ratio : p.q === t.ratio;
}

export const CHALLENGE_ROUNDS = 5;
